import jwtDecode from 'jwt-decode';
import { StorageRepository } from '@betgames/bg-tools';
import { TwainGameId } from 'betgames-contract-js';
import { PersistKey } from 'providers/Persist';
import { BettingOptionsFormat } from 'enums/BettingOptionsFormat';
import { Theme } from 'enums/Theme';
import { PlatformID } from 'integrations/enums/PlatformID';
import { ICurrency } from 'interfaces/ICurrency';
import { IClientLimits } from 'interfaces/IClientLimits';
import { ISessionState, ISettings } from './interfaces';

export default class SessionStore {
    session: ISessionState = {
        languageCode: null,
        currency: {
            template: null,
            precision: 2,
            rate: null,
            code: null,
        },
        limits: {
            minAmount: '0',
        },
        timezone: null,
        settings: {
            amount: '0',
        },
        jwt: null,
        wsUrl: null,
        visitorId: null,
        oddsFormat: BettingOptionsFormat.DECIMAL,
        showBalance: true,
        showLogo: true,
        theme: Theme.DEFAULT,
        dynamicHeight: false,
        homeUrl: null,
        gui: false,
        platformID: PlatformID.DESKTOP,
        versions: null,
    };

    private persistSession = (): void => {
        this.persistor.setItemParsed(PersistKey.session, this.session);
    };

    constructor(private readonly persistor: StorageRepository) {
        const persistedSession = this.persistor.getItemParsed<ISessionState>(PersistKey.session);

        if (persistedSession) {
            this.session = persistedSession;
        }
    }

    public initSession = (session: ISessionState): void => {
        this.session = {
            ...this.session,
            ...session,
        };

        this.persistSession();
    };

    public setSettings = (settings: ISettings) => {
        this.session.settings = settings;

        this.persistSession();
    };

    public setVisitorId = (visitorId: string) => {
        this.session.visitorId = visitorId;

        this.persistSession();
    };

    public setTheme = (theme: Theme) => {
        this.session.theme = theme;

        this.persistSession();
    };

    public setLanguage = (languageCode: string) => {
        this.session.languageCode = languageCode;

        this.persistSession();
    };

    public setCurrency = (currency: ICurrency) => {
        this.session.currency = currency;

        this.persistSession();
    };

    public setLimits = (limits: IClientLimits) => {
        this.session.limits = limits;

        this.persistSession();
    };

    public setGui = (gui: ISessionState['gui']) => {
        this.session.gui = gui;

        this.persistSession();
    };

    public getGameVersion(gameId: TwainGameId): string {
        return this.session.versions[gameId] || '';
    }

    public get language() {
        return this.session.languageCode;
    }

    public get dynamicHeight() {
        return this.session.dynamicHeight;
    }

    public get currency(): ICurrency {
        return this.session.currency;
    }

    public get currencyPrecision(): number {
        return this.session.currency?.precision;
    }

    public get currencyTemplate(): string {
        return this.session.currency?.template;
    }

    public get currencySymbol(): string {
        return this.session.currency?.template.replace(/[X ]/g, '');
    }

    public get timezone(): number {
        return Number(this.session.timezone);
    }

    public get settings(): ISettings {
        return this.session.settings;
    }

    public get defaultAmount(): string {
        return this.session?.settings?.amount || '0';
    }

    public get limits(): IClientLimits {
        return this.session.limits;
    }

    public get minAmount(): number {
        return Number(this.session.limits?.minAmount) || 0;
    }

    public get wsUrl() {
        return this.session.wsUrl;
    }

    public get visitorId() {
        return this.session.visitorId;
    }

    public get oddsFormat() {
        return this.session.oddsFormat;
    }

    public get showBalance() {
        return this.session.showBalance;
    }

    public get showLogo() {
        return this.session.showLogo;
    }

    public get theme() {
        return this.session.theme;
    }

    public get homeUrl() {
        return this.session.homeUrl;
    }

    public get gui() {
        return this.session.gui;
    }

    public get platformID() {
        return this.session.platformID;
    }

    public get userId() {
        try {
            const jwtData = jwtDecode<{ username: string }>(this.session.jwt);

            if (jwtData) {
                return Number(jwtData.username.split(':')[0]);
            }

            return 0;
        } catch {
            return 0;
        }
    }
}
